/**
 * balance-space-structures.ts — thin helper surface over
 * `state.structuresData.spaceByKind` and `state.structuresData.features`.
 *
 * v3M14c.2 migrated every per-kind cost / ticks / site requirement and
 * every feature scalar (ODP ring config, Minefield mechanics, Warp Gate
 * mesh speedup, Repair Yard radius, extractor rates) out of file-level
 * constants. Helpers below take the loaded catalogue as the first argument.
 *
 * What still lives in this file as a code constant:
 *   - String identifiers (chassis names, kinds, tech IDs) — structural
 *     facts about what entities exist, not balance numbers.
 *   - `STATIONARY_SPECIAL_ABILITY` — type discriminator marker.
 *   - `CONSTRUCTOR_CHASSIS_BY_RACE` — race → chassis mapping (could move
 *     to xlsx in a future pass; out of scope for v3M14).
 *   - Environmental seeding constants — scenario-gen knobs, not structure
 *     balance; live next to the seeder rather than the catalogue for now.
 */

import type { RaceId, StructuresData } from '../state/types/index';

/**
 * specialAbility marker stamped on every synthetic space-structure
 * ShipTemplate (Sensor Buoy, all three Weapons Platforms, Repair Yard,
 * Asteroid Mining Outpost, Gas Giant Refinery). MFix5.2 uses this as
 * the immobile-platform discriminator in the move/jump appliers.
 */
export const STATIONARY_SPECIAL_ABILITY = 'Stationary';

/**
 * Race → Constructor chassis name. Per SHIPS §3, each v1 race has a
 * single "universal space-structure builder" Class 1 chassis with a
 * different name in `ships.json`.
 */
export const CONSTRUCTOR_CHASSIS_BY_RACE: Readonly<Record<RaceId, string>> = Object.freeze({
  'RACE-AA': 'Engineer',
  'RACE-BB': 'Constructor',
  'RACE-CC': 'Mechanic',
  // v2 races — placeholder so the map is total over RaceId.
  'RACE-DD': 'Engineer',
  'RACE-EE': 'Engineer',
  // v3M21.1 — synthetic neutral empire never builds Constructors;
  // key exists only to satisfy Record<RaceId, …>.
  Neutral: '',
});

/**
 * Constructor-built space structure kinds. Each maps to a synthetic
 * ShipTemplate injected at load time + a `SpaceStructureRow` in
 * `state.structuresData.spaceByKind`. Kept as a static list because
 * which kinds exist is a structural fact (drives the UI build menu
 * + the synthetic-template injection in `loader.ts`); per-kind
 * balance now lives in the catalogue.
 */
export const SPACE_STRUCTURE_KINDS: readonly string[] = Object.freeze([
  'Sensor Buoy',
  // v4M4.e.1b — 3 category platforms (Energy / Kinetic / Concussion
  // Weapons Platform) retired in favour of 6 subType-specific platforms.
  // Each platform fires its specific weapon subType per the
  // weaponSubtypeEmitter effect; race access gated via maxLevelByRace
  // in `structures.spaceByKind` (RACE-AA: Burst + Railgun;
  // RACE-BB: Beam + Torpedo; RACE-CC: Pulse). See `docs/Structures.xlsx`
  // Space Structures sheet for the authoritative roster.
  // MFix42.2 (2026-06-01) — Missile Weapons Platform removed from data
  // layer + xlsx + UI per playtest feedback (redundant with Torpedo +
  // ship-mounted missiles). SAVE_FORMAT_VERSION bumped 32 → 33;
  // pre-v33 saves with built Missile Platforms fail-load at the
  // deserialise version gate.
  'Pulse Weapons Platform',
  'Beam Weapons Platform',
  'Railgun Weapons Platform',
  'Burst Weapons Platform',
  'Torpedo Weapons Platform',
  'Repair Yard',
  'Asteroid Mining Outpost',
  'Gas Giant Refinery',
  'Warp Gate',
  'Minefield',
]);

// ---------------------------------------------------------------------------
// Per-kind catalogue accessors
// ---------------------------------------------------------------------------

/**
 * Build cost (credits + minerals) for the given Constructor-built kind.
 * Returns `{ credits: 0, minerals: 0 }` if the catalogue lacks the row —
 * defensive default; would indicate a missing json entry.
 */
export function spaceBuildCostForKind(
  structures: StructuresData,
  kind: string,
): { credits: number; minerals: number } {
  const row = structures.spaceByKind[kind];
  if (!row) return { credits: 0, minerals: 0 };
  return { credits: row.buildCredits, minerals: row.buildMinerals };
}

/** Build time in ticks for the given Constructor-built kind. */
export function spaceBuildTicksForKind(
  structures: StructuresData,
  kind: string,
): number {
  return structures.spaceByKind[kind]?.buildTicks ?? 0;
}

/**
 * Site requirement for the given Constructor-built kind.
 * `'open'` means anywhere; `'asteroid-field'` / `'exotic-gas-cloud'`
 * mean an `EnvironmentEntity` of that kind must underlie the build
 * target. Returns `'open'` if the catalogue lacks the row.
 */
export function spaceSiteRequirementForKind(
  structures: StructuresData,
  kind: string,
): 'open' | 'asteroid-field' | 'exotic-gas-cloud' {
  return structures.spaceByKind[kind]?.siteRequirement ?? 'open';
}

// ---------------------------------------------------------------------------
// Feature accessors
// ---------------------------------------------------------------------------

/**
 * v3M18 — minerals cost debited from the issuing empire when a player
 * authors a new Warp Gate connection. See GALAXY.md §3.2 redesign.
 */
export function warpGateConnectionCostMinerals(
  structures: StructuresData,
): number {
  return structures.features.warpGate.connectionCostMinerals;
}

/**
 * v3M18 — fuel cost debited from the issuing empire when a player
 * authors a new Warp Gate connection.
 */
export function warpGateConnectionCostFuel(
  structures: StructuresData,
): number {
  return structures.features.warpGate.connectionCostFuel;
}

/**
 * v3M18 — number of ticks a `'building'` link spends in that status
 * before `advanceWarpGateLinks` flips it to `'live'`. Disconnect is
 * instant + free regardless of status.
 */
export function warpGateConnectionBuildTicks(
  structures: StructuresData,
): number {
  return structures.features.warpGate.connectionBuildTicks;
}

/** v3M2.2 — number of ring platforms an ODP deploys (STRUCTURES §3.4). */
export function odpRingCount(structures: StructuresData): number {
  return structures.features.odp.ringCount;
}

/** v3M2.2 — orbit radius of ODP ring platforms (local 0..1 in-system coords). */
export function odpOrbitRadius(structures: StructuresData): number {
  return structures.features.odp.orbitRadius;
}

/** v3M2.2 — credits + minerals cost when a destroyed ring slot regenerates. */
export function odpRebuildCost(
  structures: StructuresData,
): { credits: number; minerals: number } {
  return {
    credits: structures.features.odp.rebuildCostCredits,
    minerals: structures.features.odp.rebuildCostMinerals,
  };
}

/** v3M2.2 — ring-platform rebuild time (ticks). */
export function odpRebuildTicks(structures: StructuresData): number {
  return structures.features.odp.rebuildTicks;
}

/** v3M2.3 — radius of a minefield's hazard zone (local 0..1 in-system coords). */
export function minefieldRadius(structures: StructuresData): number {
  return structures.features.minefield.radius;
}

/** v3M2.3 — initial mine count when a minefield is deployed. */
export function mineCountInitial(structures: StructuresData): number {
  return structures.features.minefield.mineCountInitial;
}

/** v3M2.3 — per-field cap for mine count (Self Replicating regen up to this). */
export function mineCountCap(structures: StructuresData): number {
  return structures.features.minefield.mineCountCap;
}

/** v3M2.3 — flat damage per mine trigger (after armor; bypasses shields). */
export function mineDamage(structures: StructuresData): number {
  return structures.features.minefield.mineDamage;
}

/**
 * v3M2.3 — per-target trigger throttle. A ship that triggers a mine
 * cannot trigger another in the same field for this many ticks.
 */
export function mineTriggerCooldownTicks(structures: StructuresData): number {
  return structures.features.minefield.triggerCooldownTicks;
}

/** Repair Yard friendly-hull regen radius (local 0..1 in-system coords). */
export function repairYardRadius(structures: StructuresData): number {
  return structures.features.repairYard.radius;
}

/** Hull restored per tick to a friendly ship inside a Repair Yard's field. */
export function repairYardHullPerTick(structures: StructuresData): number {
  return structures.features.repairYard.hullPerTick;
}

/** Minerals per tick from one Asteroid Mining Outpost. */
export function asteroidMiningMineralsPerTick(structures: StructuresData): number {
  return structures.features.extractors.asteroidMiningMineralsPerTick;
}

/** Fuel per tick from one Gas Giant Refinery. */
export function gasGiantRefineryFuelPerTick(structures: StructuresData): number {
  return structures.features.extractors.gasGiantRefineryFuelPerTick;
}

// ---------------------------------------------------------------------------
// Chassis-name + tech-ID identifiers (string facts, not balance)
// ---------------------------------------------------------------------------

/** v3M2.1 — chassis name for the Warp Gate space structure. */
export const WARP_GATE_CHASSIS = 'Warp Gate';

/**
 * v3M2.2 — chassis name for the ring platforms spawned by an ODP. Not in
 * `SPACE_STRUCTURE_KINDS` because the player never builds them directly.
 */
export const ODP_PLATFORM_CHASSIS = 'ODP Platform';

/** v3M2.3 — chassis name for the Minefield space structure. */
export const MINEFIELD_CHASSIS = 'Minefield';

/** v3M2.3 — required tech for building Minefields. */
export const MINEFIELDS_TECH = 'Minefields';
export const MINEFIELDS_SELF_REPLICATING_TECH = 'Minefields - Self Replicating Mines';
export const MINEFIELDS_CLOAKED_TECH = 'Minefields - Cloaked Mines';
export const MINEFIELDS_IFF_TECH = 'Minefields - IFF';

// ---------------------------------------------------------------------------
// Per-system environmental seeding (scenario-gen, not structure balance)
// ---------------------------------------------------------------------------

/**
 * Per-system environmental seeding.
 *
 * Asteroid fields and gas giants are extractor-validated kinds (M14):
 *   - Asteroid fields: >=1 per system guaranteed for extractor testability.
 *   - Gas giants: 1-2 per system (MFix5.4) so a Refinery is always buildable.
 *
 * MFix4.2 — exotic cosmetic terrain (PLANETS.md):
 *   - Nebula: 0 or 1 per system — common terrain feature.
 *   - Ion cloud: 0 or 1 per system — uncommon hostile pocket.
 *   - Radiation belt: 0 or 1 per system — uncommon hostile pocket.
 *   - Black hole: rare galaxy-wide; rolled per system at 8% chance.
 *
 * Scenario-gen tunables, scoped out of the v3M14 structures catalogue —
 * v3M10 balance pass may revisit.
 *
 * v3M10.1: all migrated to state.balanceData.scenario.* fields.
 */
